import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Container, Section, SectionHeader } from './primitives';
import { formatBRL } from './richText';
import { landing } from './tokens';

const Panel: React.FC<{ children: React.ReactNode; tint?: boolean }> = ({
  children,
  tint = false,
}) => (
  <Box
    sx={{
      p: 2,
      borderRadius: '16px',
      border: tint ? 'none' : `1px solid ${landing.line}`,
      bgcolor: tint ? landing.accentSoft : 'transparent',
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
    }}
  >
    {children}
  </Box>
);

const Big: React.FC<{ children: React.ReactNode; accent?: boolean }> = ({
  children,
  accent = false,
}) => (
  <Box
    component='span'
    sx={{
      fontFamily: landing.display,
      fontSize: 24,
      fontWeight: 700,
      color: accent ? landing.accent : landing.ink,
    }}
  >
    {children}
  </Box>
);

const Small: React.FC<{ children: React.ReactNode; strong?: boolean }> = ({
  children,
  strong = false,
}) => (
  <Box
    component='span'
    sx={{ fontSize: 13, fontWeight: strong ? 700 : 400, color: strong ? landing.ink : landing.ink3 }}
  >
    {children}
  </Box>
);

const categories = [
  { key: 'housing', amount: 1640, width: '100%' },
  { key: 'food', amount: 1212.4, width: '74%' },
  { key: 'transport', amount: 586.3, width: '36%' },
  { key: 'leisure', amount: 431, width: '26%' },
];

const upcoming = [
  { key: 'power', amount: 230, urgent: true },
  { key: 'internet', amount: 99.9, urgent: false },
  { key: 'laptop', amount: 420, urgent: false },
];

/** A stylized picture of the web dashboard, not the real component. */
const DashboardPreview: React.FC = () => {
  const { t, i18n } = useTranslation('landing');
  const money = (value: number) => formatBRL(value, i18n.language);
  const contexts = [
    { key: 'personal', active: true },
    { key: 'home', active: false },
    { key: 'trip', active: false, group: true },
  ];
  return (
    <Section sx={{ bgcolor: landing.bg2 }}>
      <Container sx={{ display: 'flex', flexDirection: 'column', gap: 5.5 }}>
        <SectionHeader
          eyebrow={t('dashboard.eyebrow')}
          title={t('dashboard.title')}
          lead={t('dashboard.description')}
        />
        <Box
          role='img'
          aria-label={t('dashboard.imageLabel')}
          sx={{
            bgcolor: landing.surface,
            border: `1px solid ${landing.line}`,
            borderRadius: '22px',
            boxShadow: landing.shadow,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${landing.line}`,
              bgcolor: landing.bg,
            }}
          >
            {[0, 1, 2].map((dot) => (
              <Box
                key={dot}
                sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: landing.line }}
              />
            ))}
            <Box component='span' sx={{ ml: 1.25, fontSize: 13, color: landing.ink3 }}>
              {t('dashboard.windowTitle')}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            <Box
              sx={{
                flex: '1 1 200px',
                maxWidth: '100%',
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                borderRight: `1px solid ${landing.line}`,
                bgcolor: landing.bg,
              }}
            >
              <Box
                component='span'
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: landing.ink3,
                  px: 1.25,
                  pb: 0.75,
                }}
              >
                {t('dashboard.contexts')}
              </Box>
              {contexts.map((context) => (
                <Box
                  key={context.key}
                  component='span'
                  sx={{
                    p: 1.25,
                    borderRadius: '10px',
                    fontSize: 14,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1,
                    bgcolor: context.active ? landing.accentSoft : 'transparent',
                    color: context.active ? landing.accent : landing.ink2,
                    fontWeight: context.active ? 700 : 400,
                  }}
                >
                  {t(`dashboard.${context.key}`)}
                  {context.group && (
                    <Box component='span' sx={{ fontSize: 11, fontWeight: 700, color: landing.ink3 }}>
                      {t('dashboard.groupTag')}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                flex: '999 1 560px',
                minWidth: 0,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2.25,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
                <Box
                  component='span'
                  sx={{
                    fontFamily: landing.display,
                    fontSize: 22,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {t('dashboard.month')}
                </Box>
                <Small>{t('dashboard.personal')}</Small>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(170px, 100%), 1fr))',
                  gap: 1.5,
                }}
              >
                <Panel>
                  <Small>{t('dashboard.expenses')}</Small>
                  <Big>{money(4832.1)}</Big>
                </Panel>
                <Panel>
                  <Small>{t('dashboard.income')}</Small>
                  <Big>{money(7200)}</Big>
                </Panel>
                <Panel tint>
                  <Small>{t('dashboard.leftover')}</Small>
                  <Big accent>{money(1618)}</Big>
                </Panel>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
                  gap: 1.5,
                }}
              >
                <Panel>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Small strong>{t('dashboard.cumulative')}</Small>
                    <Small>{t('dashboard.vsLastMonth')}</Small>
                  </Box>
                  <svg viewBox='0 0 300 120' width='100%' height='120' preserveAspectRatio='none' aria-hidden='true'>
                    <polyline
                      points='0,112 40,100 80,88 120,76 160,62 200,48 240,36 300,22'
                      fill='none'
                      stroke={landing.ink3}
                      strokeWidth='2'
                      strokeDasharray='4 5'
                    />
                    <polyline
                      points='0,112 30,104 60,96 90,86 120,80 150,70 180,62 210,54 242,46'
                      fill='none'
                      stroke={landing.accent}
                      strokeWidth='3'
                      strokeLinecap='round'
                    />
                    <circle cx='242' cy='46' r='4.5' fill={landing.accent} />
                  </svg>
                </Panel>
                <Panel>
                  <Small strong>{t('dashboard.byCategory')}</Small>
                  {categories.map((category) => (
                    <Box key={category.key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <Box component='span' sx={{ color: landing.ink2 }}>
                          {t(`dashboard.categories.${category.key}`)}
                        </Box>
                        <span>{money(category.amount)}</span>
                      </Box>
                      <Box sx={{ height: 6, borderRadius: 99, bgcolor: landing.bg2 }}>
                        <Box
                          sx={{
                            width: category.width,
                            height: 6,
                            borderRadius: 99,
                            bgcolor: landing.accent,
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Panel>
              </Box>

              <Panel>
                <Small strong>{t('dashboard.upcoming')}</Small>
                {upcoming.map((bill) => (
                  <Box
                    key={bill.key}
                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, fontSize: 14 }}
                  >
                    <span>
                      {t(`dashboard.bills.${bill.key}.name`)}{' '}
                      <Box
                        component='span'
                        sx={{
                          color: bill.urgent ? landing.warn : landing.ink3,
                          fontWeight: bill.urgent ? 700 : 400,
                        }}
                      >
                        · {t(`dashboard.bills.${bill.key}.due`)}
                      </Box>
                    </span>
                    <span>{money(bill.amount)}</span>
                  </Box>
                ))}
              </Panel>
            </Box>
          </Box>
        </Box>
      </Container>
    </Section>
  );
};

export default DashboardPreview;
